import { sortBy } from "lodash-es";
import { useCallback } from "react";
import { uuidv4 } from "../../../../../utils/uuid";
import { useAccountId } from "../../../../generic/hooks/use-account-id";

import type { NoNullFields } from "../../../../../server/supabase/base-supabase-service";
import type { UUID } from "../../../../../utils/uuid";
import type { UsableAccountPersonField } from "../../../server/supabase-segment-field-service";
import type { Condition } from "../../../types/segment-schema";

type WithDisabled = NoNullFields<UsableAccountPersonField> & { disabled: boolean };

// Helper for missing fields
function missingConditionToSegmentField(
  condition: Condition,
  account_id: UUID,
): NoNullFields<WithDisabled> {
  return {
    id: uuidv4(),
    key: condition.field,
    display_name: (condition.field.startsWith("event_")
      ? condition.field.replace("event_", "")
      : condition.field
    )
      .replace("recipient_type_", "")
      .replace("_", " "),
    field_type: condition.field.startsWith("event_") ? "DATE" : "TEXT",
    usable: false,
    disabled: true,
    account_id,
  };
}

export type UseMakeSortedSegmentFields = (c: Condition) => {
  fields: NoNullFields<WithDisabled>[];
  groupingFn: (option: NoNullFields<WithDisabled>) => string;
  deprecatedField?: NoNullFields<WithDisabled>;
  currentField: {
    type: "valid" | "missing";
    field: NoNullFields<WithDisabled>;
  };
};

export function useMakeSortedSegmentFields(
  allFields: NoNullFields<UsableAccountPersonField>[],
): UseMakeSortedSegmentFields {
  const account_id = useAccountId();

  const makeFieldsForCondition = useCallback(
    (condition: Condition) => {
      const deprecatedField = allFields.find((f) => f.key === condition.field && !f.usable);
      const field = allFields.find((f) => f.key === condition.field);
      const currentField = field
        ? {
            type: "valid" as const,
            field: { ...field, disabled: deprecatedField?.key === field.key },
          }
        : {
            type: "missing" as const,
            field: missingConditionToSegmentField(condition, account_id!),
          };

      const baseFields = [
        ...allFields,
        ...(currentField.type === "missing"
          ? [missingConditionToSegmentField(condition, account_id!)]
          : []),
      ].filter((f) => f.key === deprecatedField?.key || f.key === condition.field || f.usable);

      const fields = sortBy(baseFields, [
        (f) => {
          if (f.key === deprecatedField?.key) return 3;
          if (f.key?.startsWith("event")) return 1;
          if (f.key?.startsWith("recipient_type")) return 2;
          return 0;
        },
        (f) => f.display_name,
      ]).map((f) => ({
        ...f,
        disabled:
          f.key === deprecatedField?.key ||
          (currentField.type === "missing" && f.key === condition.field),
      }));

      const groupingFn = (option: NoNullFields<WithDisabled>): string => {
        if (option.key === deprecatedField?.key) return "Deprecated fields";
        if (option.key.startsWith("event")) return "Events";
        if (option.key.startsWith("recipient_type")) return "Audience";
        return "Fields";
      };

      return {
        fields,
        groupingFn,
        deprecatedField: deprecatedField
          ? {
              ...deprecatedField,
              disabled: true,
            }
          : undefined,
        currentField,
      };
    },
    [allFields, account_id],
  );

  return makeFieldsForCondition;
}
